import { Link } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { Wordmark } from "@/components/brand-mark";
import { cn } from "@/lib/utils";

/**
 * Four items, named for what someone is trying to do rather than for how the site is
 * organised. V1 had eight flat nouns ("Tick ID", "Diseases", "Alpha-Gal", "Bite
 * Tool"…), which asked the reader to translate their situation into our taxonomy
 * before they could click anything.
 *
 * Nothing was removed — everything V1 linked to is still one click away, in the More
 * menu here and in the footer.
 */
const PRIMARY = [
  { to: "/map", label: "Map" },
  { to: "/tick-identification", label: "Identify a tick" },
  { to: "/tick-bite-tool", label: "I was bitten" },
  { to: "/prevention", label: "Prevent" },
] as const;

const MORE = [
  { to: "/alpha-gal", label: "Alpha-gal syndrome" },
  { to: "/ticks", label: "Tick species guide" },
  { to: "/diseases", label: "Tick-borne diseases" },
  { to: "/tick-bite-guide", label: "Printable bite checklist" },
  { to: "/states", label: "Every state" },
  { to: "/resources", label: "Articles & resources" },
  { to: "/sources", label: "Sources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
] as const;

/* Text links with an underline that marks the current page — the way a printed index
   marks the current section, rather than a filled pill behind it. */
const LINK =
  "relative px-3 py-2 text-[0.9375rem] font-medium text-foreground/80 transition-surface hover:text-foreground after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:bg-transparent after:transition-colors";
const LINK_ACTIVE = "text-foreground after:!bg-rust";

/**
 * A disclosure, not a menu widget — these are ordinary navigation links, so a button
 * with aria-expanded over a plain list is both correct and 30 lines. The Radix
 * dropdown this replaced was ~10KB gzipped in the shared entry chunk, on every page,
 * for one header control.
 */
function MoreMenu() {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className={`${LINK} inline-flex items-center gap-1`}
      >
        More
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      <div
        id={id}
        hidden={!open}
        className="absolute right-0 top-full z-50 mt-2 w-64 border border-border bg-popover py-2 shadow-lift"
      >
        <ul>
          {MORE.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-foreground transition-surface hover:bg-secondary"
                activeProps={{ className: "font-semibold text-primary" }}
              >
                {n.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="no-print sticky top-0 z-40 border-b border-border bg-background/92 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
      <a
        href="#main"
        className="sr-only z-50 bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground focus:not-sr-only focus:absolute focus:left-4 focus:top-3"
      >
        Skip to content
      </a>
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link to="/" aria-label="LoneStarMap home" className="-ml-1 rounded-sm px-1 py-1">
          <Wordmark />
        </Link>
        <div className="flex items-center gap-1">
          <nav aria-label="Main" className="hidden items-center lg:flex">
            {PRIMARY.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className={`${LINK} h-16 leading-[3rem]`}
                activeProps={{ className: LINK_ACTIVE }}
              >
                {n.label}
              </Link>
            ))}
            <MoreMenu />
          </nav>
          <ThemeToggle className="ml-2" />
          <button
            onClick={() => setOpen((v) => !v)}
            className="ml-1 inline-flex h-10 items-center gap-2 rounded-sm px-2 text-sm font-semibold text-foreground lg:hidden"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <span aria-hidden className="relative block h-3 w-4">
              <span
                className={cn(
                  "absolute left-0 top-0 h-[1.5px] w-4 bg-current transition-transform",
                  open && "top-[5px] rotate-45",
                )}
              />
              <span
                className={cn(
                  "absolute bottom-0 left-0 h-[1.5px] w-4 bg-current transition-transform",
                  open && "bottom-[5px] -rotate-45",
                )}
              />
            </span>
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>
      {open && (
        <nav aria-label="Main" className="border-t border-border bg-background lg:hidden">
          <div className="mx-auto max-w-6xl px-4 pb-6 pt-2 sm:px-6">
            <ul className="divide-y divide-border">
              {PRIMARY.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className="block py-3.5 font-display text-2xl font-medium text-foreground"
                    activeProps={{ className: "text-primary" }}
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-1">
              {MORE.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className="block py-2 text-sm text-foreground/85 hover:text-primary"
                    activeProps={{ className: "font-semibold text-primary" }}
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      )}
    </header>
  );
}
